import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatDateID, formatTimeID } from "@/lib/format";
import { toast } from "sonner";
import { Star, Search, EyeOff, Eye, MessageSquareReply, Trash2, AlertTriangle, Sparkles } from "lucide-react";

const inp = "h-9 px-3 rounded-lg bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-[#C4738A]";
const PINK = "#C4738A";

const PERIODS = [
  { id: "7", label: "7 Hari Terakhir" },
  { id: "30", label: "30 Hari Terakhir" },
  { id: "90", label: "90 Hari Terakhir" },
  { id: "all", label: "Semua Waktu" },
];

function Stars({ n, size = "w-3.5 h-3.5" }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`${size} ${i <= n ? "text-amber-400 fill-amber-400" : "text-slate-200 fill-slate-200"}`} />
      ))}
    </div>
  );
}

function Delta({ v }) {
  if (v === null || v === undefined) return <span className="text-[10px] text-slate-300">vs periode sebelumnya —</span>;
  const up = v >= 0;
  return <span className="text-[10px] text-slate-400">vs periode sebelumnya <b className={up ? "text-emerald-500" : "text-red-500"}>{up ? "▲" : "▼"} {Math.abs(v)}%</b></span>;
}

export default function AdminReviews() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("all");
  const [pend, setPend] = useState({ q: "", period: "30", stars: new Set() });
  const [applied, setApplied] = useState({ q: "", period: "30", stars: "" });
  const [replying, setReplying] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/reviews", { params: { period: applied.period, status: tab, q: applied.q, stars: applied.stars } })
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], stats: null, tab_counts: {}, star_counts: {} }));
  }, [applied, tab]);
  useEffect(() => { load(); }, [load]);

  const apply = () => setApplied({ q: pend.q, period: pend.period, stars: [...pend.stars].sort().join(",") });
  const reset = () => { const d = { q: "", period: "30", stars: new Set() }; setPend(d); setApplied({ q: "", period: "30", stars: "" }); setTab("all"); };
  const toggleStar = (n) => setPend((p) => { const s = new Set(p.stars); s.has(n) ? s.delete(n) : s.add(n); return { ...p, stars: s }; });

  const seeBad = () => { setTab("pending"); setPend((p) => ({ ...p, stars: new Set([1, 2, 3]) })); setApplied((a) => ({ ...a, stars: "1,2,3" })); };
  const seeNew = () => { setTab("all"); setPend((p) => ({ ...p, stars: new Set() })); setApplied((a) => ({ ...a, stars: "" })); };

  const sendReply = async (r) => {
    if (replyText.trim().length < 2) return toast.error("Balasan minimal 2 karakter");
    setBusy(true);
    try {
      await api.post(`/admin/reviews/${r.id}/reply`, { text: replyText.trim() });
      toast.success("Balasan terkirim — tampil di halaman produk");
      setReplying(null); setReplyText("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengirim balasan"); }
    finally { setBusy(false); }
  };
  const delReply = async (r) => {
    try {
      await api.delete(`/admin/reviews/${r.id}/reply`);
      toast.success("Balasan dihapus");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus balasan"); }
  };
  const toggleHide = async (r) => {
    try {
      await api.patch(`/admin/reviews/${r.id}`, { hidden: !r.hidden });
      toast.success(r.hidden ? "Ulasan ditampilkan kembali" : "Ulasan disembunyikan dari toko");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengubah ulasan"); }
  };

  const s = data?.stats;
  const tc = data?.tab_counts || {};
  const sc = data?.star_counts || {};
  const rangeLabel = applied.period === "all" ? "Semua data ulasan" : (() => {
    const end = new Date(); const st = new Date(); st.setDate(st.getDate() - parseInt(applied.period));
    const f = (d) => d.toLocaleDateString("id-ID", { day: "2-digit", month: "2-digit", year: "numeric" });
    return `Data dari ${f(st)} sampai ${f(end)}`;
  })();

  return (
    <div data-testid="reviews-page" className="space-y-5 pb-10">
      {/* Header ringkasan */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5 flex items-center gap-4 flex-wrap">
        <div className="flex items-end gap-2">
          <span className="font-display text-sm font-semibold text-slate-700">Penilaian Toko</span>
          <span data-testid="reviews-avg" className="font-display text-3xl font-bold text-[#C4738A]">{s ? s.avg : "…"}</span>
          <span className="text-sm text-slate-400 mb-1">/5</span>
        </div>
        {s && <Stars n={Math.round(s.avg)} size="w-4 h-4" />}
        <span className="text-[11px] text-slate-400 ml-auto">{rangeLabel}</span>
      </div>

      {/* 3 kartu statistik */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-white rounded-2xl border border-slate-100 p-5">
          <div className="text-[11px] text-slate-400 font-medium">Penilaian Diterima</div>
          <div data-testid="stat-total" className="font-display text-2xl font-bold text-slate-900 mt-1">{s ? s.total.toLocaleString("id-ID") : "…"}</div>
          <Delta v={s?.delta} />
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-5">
          <div className="text-[11px] text-slate-400 font-medium">Tingkat Penilaian Pesanan</div>
          <div data-testid="stat-rate" className="font-display text-2xl font-bold text-slate-900 mt-1">{s ? `${s.rate_pct}%` : "…"}</div>
          <span className="text-[10px] text-slate-300">pesanan selesai yang diulas</span>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-5">
          <div className="text-[11px] text-slate-400 font-medium">Tingkat Penilaian Baik</div>
          <div data-testid="stat-good" className="font-display text-2xl font-bold text-slate-900 mt-1">{s ? `${s.good_pct}%` : "…"}</div>
          <span className="text-[10px] text-slate-300">ulasan bintang 4–5</span>
        </div>
      </div>

      {/* 2 kartu aksi */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white rounded-2xl border border-slate-100 p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-red-50 text-red-500 flex items-center justify-center flex-shrink-0"><AlertTriangle className="w-5 h-5" /></div>
          <div className="flex-1">
            <div className="text-[11px] text-slate-400 font-medium">Penilaian Buruk yang Belum Dibalas</div>
            <div className="flex items-baseline gap-2">
              <span data-testid="stat-bad" className="font-display text-2xl font-bold text-red-500">{s ? s.bad_unreplied.toLocaleString("id-ID") : "…"}</span>
              <button data-testid="see-bad" onClick={seeBad} className="text-[11px] font-semibold text-[#C4738A] hover:underline">Lihat ›</button>
            </div>
            <div className="text-[10px] text-slate-300">Jumlah penilaian di bawah 4 yang belum dibalas</div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-rose-50 text-[#C4738A] flex items-center justify-center flex-shrink-0"><Sparkles className="w-5 h-5" /></div>
          <div className="flex-1">
            <div className="text-[11px] text-slate-400 font-medium">Penilaian Baru</div>
            <div className="flex items-baseline gap-2">
              <span data-testid="stat-new" className="font-display text-2xl font-bold text-slate-900">{s ? s.new_count.toLocaleString("id-ID") : "…"}</span>
              <button data-testid="see-new" onClick={seeNew} className="text-[11px] font-semibold text-[#C4738A] hover:underline">Lihat ›</button>
            </div>
            <div className="text-[10px] text-slate-300">Penilaian masuk pada periode terpilih</div>
          </div>
        </div>
      </div>

      {/* Daftar Penilaian */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5 space-y-4">
        <h2 className="font-display font-bold text-sm text-slate-900">Daftar Penilaian Toko</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] text-slate-400 mr-1">Status</span>
          {[
            { id: "all", label: `Semua (${tc.all ?? 0})` },
            { id: "pending", label: `Perlu Dibalas (${tc.pending ?? 0})` },
            { id: "replied", label: `Sudah Dibalas (${tc.replied ?? 0})` },
          ].map((t) => (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`h-8 px-4 rounded-full text-[11px] font-semibold border transition-colors ${tab === t.id ? "border-[#C4738A] text-[#C4738A] bg-rose-50/50" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] text-slate-400 mr-1">Penilaian Produk</span>
          <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
            <input type="checkbox" data-testid="star-all" checked={pend.stars.size === 0} onChange={() => setPend((p) => ({ ...p, stars: new Set() }))} className="w-3.5 h-3.5 rounded border-slate-300 accent-[#C4738A]" />
            Semua
          </label>
          {[5, 4, 3, 2, 1].map((n) => (
            <label key={n} className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
              <input type="checkbox" data-testid={`star-${n}`} checked={pend.stars.has(n)} onChange={() => toggleStar(n)} className="w-3.5 h-3.5 rounded border-slate-300 accent-[#C4738A]" />
              {n} Bintang ({sc[String(n)] ?? 0})
            </label>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-3.5 h-3.5 text-slate-300 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="review-search" value={pend.q} onChange={(e) => setPend((p) => ({ ...p, q: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && apply()}
              placeholder="Cari Produk, No. Pesanan, Nama Pembeli" className={`${inp} w-full pl-8`} />
          </div>
          <select data-testid="review-period" value={pend.period} onChange={(e) => setPend((p) => ({ ...p, period: e.target.value }))} className={`${inp} w-48`}>
            {PERIODS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          <button data-testid="review-apply" onClick={apply} className="h-9 px-5 rounded-lg bg-[#C4738A] hover:bg-[#b3647b] text-white text-xs font-semibold transition-colors">Terapkan</button>
          <button data-testid="review-reset" onClick={reset} className="h-9 px-4 rounded-lg border border-slate-200 text-slate-500 text-xs font-semibold hover:border-[#C4738A] transition-colors">Atur Ulang</button>
        </div>

        <div className="hidden md:grid grid-cols-[230px_1fr_130px] gap-4 px-3 py-2 text-[10px] uppercase tracking-wide text-slate-400 border-y border-slate-100">
          <span>Informasi Produk</span><span>Penilaian Pembeli</span><span className="text-right">Tindakan</span>
        </div>

        <div className="space-y-3">
          {data === null && <div className="py-14 text-center text-xs text-slate-400">Memuat...</div>}
          {data?.items?.length === 0 && <div className="py-14 text-center text-xs text-slate-400">Tidak ada penilaian yang cocok dengan filter.</div>}
          {data?.items?.map((r) => (
            <div key={r.id} data-testid={`review-card-${r.id}`} className={`grid md:grid-cols-[230px_1fr_130px] gap-4 p-3 rounded-xl border ${r.hidden ? "border-slate-100 bg-slate-50/60 opacity-70" : "border-slate-100"}`}>
              <div className="flex gap-2.5">
                <div className="w-12 h-12 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                  {r.product_image && <img src={r.product_image} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-slate-800 leading-snug line-clamp-2">{r.product_name}</div>
                  {r.variant && <div className="text-[10px] text-slate-400 mt-0.5">Variasi: {r.variant}</div>}
                </div>
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Stars n={r.rating} />
                  <span className="text-[11px] font-semibold text-slate-700">{r.user_name || "Pembeli"}</span>
                  {r.order_code && <span className="text-[10px] text-slate-400">No. Pesanan {r.order_code}</span>}
                  {r.hidden && <span className="text-[9px] font-bold bg-red-100 text-red-500 rounded-full px-2 py-0.5">Tersembunyi</span>}
                </div>
                {r.comment && <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{r.comment}</p>}
                <div className="text-[10px] text-slate-400 mt-1.5">{formatDateID(r.created_at)} {formatTimeID(r.created_at)} • Membantu ({r.helpful || 0})</div>
                {r.reply && replying !== r.id && (
                  <div className="mt-2.5 rounded-xl bg-rose-50/60 border border-rose-100 p-3" data-testid={`reply-box-${r.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold text-[#C4738A]">Balasan Penjual</span>
                      <span className="flex gap-2">
                        <button data-testid={`reply-edit-${r.id}`} onClick={() => { setReplying(r.id); setReplyText(r.reply.text); }} className="text-[10px] font-semibold text-slate-400 hover:text-[#C4738A]">Ubah</button>
                        <button data-testid={`reply-del-${r.id}`} onClick={() => delReply(r)} className="text-[10px] font-semibold text-slate-400 hover:text-red-500">Hapus</button>
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-600 mt-1 leading-relaxed">{r.reply.text}</p>
                    <div className="text-[9px] text-slate-400 mt-1">{r.reply.by} • {formatDateID(r.reply.at)} {formatTimeID(r.reply.at)}</div>
                  </div>
                )}
                {replying === r.id && (
                  <div className="mt-2.5 space-y-2">
                    <textarea data-testid={`reply-input-${r.id}`} value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={2} autoFocus
                      placeholder="Tulis balasan sebagai penjual (tampil di halaman produk)..." className={`${inp} w-full h-auto py-2 resize-none`} />
                    <div className="flex gap-2">
                      <button data-testid={`reply-send-${r.id}`} onClick={() => sendReply(r)} disabled={busy}
                        className="h-8 px-4 rounded-lg bg-[#C4738A] hover:bg-[#b3647b] text-white text-[11px] font-semibold disabled:opacity-50">Kirim Balasan</button>
                      <button onClick={() => { setReplying(null); setReplyText(""); }} className="h-8 px-3 rounded-lg border border-slate-200 text-slate-500 text-[11px] font-semibold">Batal</button>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex md:flex-col gap-2 md:items-end">
                {replying !== r.id && (
                  <button data-testid={`reply-btn-${r.id}`} onClick={() => { setReplying(r.id); setReplyText(r.reply?.text || ""); }}
                    className="h-8 px-3 rounded-lg border border-slate-200 text-slate-600 text-[11px] font-semibold flex items-center gap-1.5 hover:border-[#C4738A] hover:text-[#C4738A] transition-colors">
                    <MessageSquareReply className="w-3.5 h-3.5" /> Balas
                  </button>
                )}
                <button data-testid={`hide-btn-${r.id}`} onClick={() => toggleHide(r)}
                  className={`h-8 px-3 rounded-lg text-[11px] font-semibold flex items-center gap-1.5 transition-colors ${r.hidden ? "bg-emerald-50 border border-emerald-200 text-emerald-600" : "border border-slate-200 text-slate-400 hover:border-red-200 hover:text-red-500"}`}>
                  {r.hidden ? <><Eye className="w-3.5 h-3.5" /> Tampilkan</> : <><EyeOff className="w-3.5 h-3.5" /> Sembunyikan</>}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
